import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { LocateFixed, X } from "lucide-react";
import { OSM_ATTRIBUTION, OSM_TILE_URL } from "../../lib/mapDefaults";

export type LatLng = { lat: number; lng: number };

interface Props {
  /** The event's current point (the picker opens Pinned there), else null (Empty). */
  initial: LatLng | null;
  /** Map centre when there is no point and my position is unavailable or denied. */
  fallbackCenter: [number, number];
  onConfirm(point: LatLng): void;
  onRemove(): void;
  onCancel(): void;
}

export const formatCoords = (p: LatLng) => `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}`;

// Figma 3039:2819: primary-colour pin; my position = blue dot (status/verified token).
const PIN_PATH =
  "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z";
const pinIcon = () =>
  L.divIcon({
    className: "",
    html: `<svg width="40" height="40" viewBox="0 0 24 24" style="filter:drop-shadow(0 1px 1px rgba(15,23,42,.25))"><path fill="var(--primary-fill)" d="${PIN_PATH}"/></svg>`,
    iconSize: [40, 40],
    iconAnchor: [20, 38],
  });
const meIcon = () =>
  L.divIcon({
    className: "",
    html: '<div style="width:16px;height:16px;border-radius:50%;background:var(--verified);border:3px solid var(--bg-surface);box-shadow:0 0 0 1px var(--verified)"></div>',
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

function TapToPin({ onTap }: { onTap(p: LatLng): void }) {
  // The world repeats sideways past ±180: wrap so a tap on a copy stores a valid longitude.
  useMapEvents({
    click: (e) => {
      const p = e.latlng.wrap();
      onTap({ lat: p.lat, lng: p.lng });
    },
  });
  return null;
}

/** Re-centres the map on a fresh position (my position arrives after the first paint; the my-location button). */
function Recenter({ target }: { target: [number, number] | null }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.setView(target, Math.max(map.getZoom?.() ?? 15, 15));
  }, [map, target]);
  return null;
}

/**
 * Figma 3039:2819 "Location picker (web modal)": tap the map to pin the event's place; Confirm sets it, Remove place
 * clears it, Cancel / ✕ / scrim / Escape close with no change.
 */
export default function LocationPickerModal({ initial, fallbackCenter, onConfirm, onRemove, onCancel }: Props) {
  const { t } = useTranslation();
  const title = t("event_picker_title");
  const [pin, setPin] = useState<LatLng | null>(initial);
  const [me, setMe] = useState<[number, number] | null>(null);
  const [locateFailed, setLocateFailed] = useState(false);
  const [recenter, setRecenter] = useState<[number, number] | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const icon = useMemo(pinIcon, []);
  const dot = useMemo(meIcon, []);

  useEffect(() => closeRef.current?.focus(), []);

  const alive = useRef(true);
  const locate = (centre: boolean, explicit: boolean) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      if (explicit) setLocateFailed(true);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (!alive.current) return;
        const p: [number, number] = [pos.coords.latitude, pos.coords.longitude];
        setLocateFailed(false);
        setMe(p);
        if (centre) setRecenter(p);
      },
      () => {
        if (alive.current && explicit) setLocateFailed(true);
      },
      { timeout: 8000 },
    );
  };
  // Opening without a point: centre on my position when the browser gives it, else the fallback (silently).
  useEffect(() => {
    alive.current = true;
    locate(initial == null, false);
    return () => { alive.current = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape closes only the picker: capture on window so the editor's own Escape handler never sees it.
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      cancelRef.current();
    };
    window.addEventListener("keydown", h, true);
    return () => window.removeEventListener("keydown", h, true);
  }, []);

  const center: [number, number] = initial ? [initial.lat, initial.lng] : fallbackCenter;

  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-[color-mix(in_srgb,var(--foreground)_40%,transparent)] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid="location-picker"
        className="flex max-h-full w-[560px] max-w-full flex-col gap-3 rounded-2xl bg-[var(--bg-surface)] p-4 shadow-[0_4px_14px_rgba(15,23,42,0.18)]"
      >
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-bold leading-[1.2] text-[var(--foreground)]">{title}</h2>
          <button
            type="button"
            ref={closeRef}
            data-testid="location-picker-close"
            aria-label={t("close")}
            onClick={onCancel}
            className="flex h-8 w-8 items-center justify-center rounded-full text-[var(--foreground-muted)]"
          >
            <X size={20} aria-hidden />
          </button>
        </div>
        <div className="relative h-[360px] overflow-hidden rounded-xl bg-[var(--bg-elevated)]">
          <MapContainer center={center} zoom={initial ? 16 : 15} minZoom={3} style={{ height: "100%", width: "100%" }}>
            <TileLayer attribution={OSM_ATTRIBUTION} url={OSM_TILE_URL} noWrap />
            <TapToPin onTap={setPin} />
            <Recenter target={recenter} />
            {me && <Marker position={me} icon={dot} interactive={false} />}
            {pin && <Marker position={[pin.lat, pin.lng]} icon={icon} />}
          </MapContainer>
          <button
            type="button"
            data-testid="location-picker-locate"
            aria-label={t("event_picker_my_location")}
            onClick={() => locate(true, true)}
            className="absolute bottom-3 right-3 z-[1000] flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-surface)] text-[var(--primary)] shadow-[0_1px_4px_rgba(15,23,42,0.25)] dark:text-[var(--primary-on-surface)]"
          >
            <LocateFixed size={20} aria-hidden />
          </button>
        </div>
        <p data-testid="location-picker-hint" className="text-sm text-[var(--foreground-muted)]">
          {pin ? t("event_picker_hint_pinned", { coords: formatCoords(pin) }) : t("event_picker_hint_empty")}
        </p>
        {locateFailed && (
          <p data-testid="location-picker-locate-failed" className="-mt-2 text-xs text-[var(--foreground-muted)]">{t("event_picker_locate_failed")}</p>
        )}
        <div className="flex items-center gap-2">
          {pin && (
            <button
              type="button"
              data-testid="location-picker-remove"
              onClick={onRemove}
              className="h-12 px-2 text-sm font-bold text-[var(--destructive-text)]"
            >
              {t("event_picker_remove")}
            </button>
          )}
          <span className="flex-1" />
          <button
            type="button"
            data-testid="location-picker-cancel"
            onClick={onCancel}
            className="h-12 px-2 text-sm font-bold text-[var(--primary)] dark:text-[var(--primary-on-surface)]"
          >
            {t("cancel")}
          </button>
          <button
            type="button"
            data-testid="location-picker-confirm"
            disabled={!pin}
            onClick={() => pin && onConfirm(pin)}
            className="h-12 rounded-full bg-[var(--primary-fill)] px-6 text-sm font-bold text-[var(--primary-foreground)] disabled:opacity-50"
          >
            {t("event_picker_confirm")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
